import React, { useMemo } from 'react';
import { Trash2, Search, FileText } from 'lucide-react';

/**
 * PartsConfigTable Component
 * Highly configurable table for BOM, MOC, and Pickup Challan management.
 * 
 * Modes: 
 * - 'sales': Focus on Base Qty and Multiplier (used in Order BOM Modal)
 * - 'moc': Focus on Order Qty, New vs Rental selection (used in MOC Page)
 * - 'inward': Focus on New, Rental, Repairable, and Non Repairable (used in MIC Page)
 * - 'pickup': Focus on Good vs Damage lifted quantities (used in Pickup Challan)
 * - 'transfer': Simple Sr No, SKU, Description, Qty (used in Inventory Transfer)
 * - 'preview': Read-only view with basic totals (used in Dashboards/Details)
 */
const PartsConfigTable = ({ 
  mode = 'preview', 
  components = [], 
  onChange, 
  onDelete, 
  multiplier = 1,
  showTotals = true,
  hideActions = false,
  pickupOutcomeReadOnly = false,
  pickupMaterialQtyReadOnly = false
}) => {

  // --- STYLES (Centralized for consistency) ---
  const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
  const thStyle = { 
    padding: '12px 10px', 
    fontSize: '11px', 
    fontWeight: '700', 
    color: '#64748b', 
    textTransform: 'uppercase', 
    borderBottom: '2px solid #f3f4f6',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    position: 'sticky',
    top: 0,
    zIndex: 1
  };
  const tdStyle = { 
    padding: '12px 10px', 
    fontSize: '13px', 
    color: '#374151', 
    textAlign: 'center', 
    borderBottom: '1px solid #f3f4f6' 
  };
  const inputStyle = { 
    width: '70px', 
    padding: '6px', 
    borderRadius: '4px', 
    border: '1px solid #d1d5db', 
    textAlign: 'center', 
    outline: 'none',
    fontSize: '13px'
  };

  const getPickupActiveQty = (component = {}) => {
    const materialQty = Number(component.quantity || 0);
    const goodQty = Number(component.liftedGood || 0);
    const damageQty = Number(component.liftedDamage || 0);
    const missingQty = Number(component.finalMissing || 0);

    return Math.max(0, materialQty - goodQty - damageQty - missingQty);
  };

  // --- CALCULATIONS ---
  const totals = useMemo(() => {
    return components.reduce((acc, c) => {
      acc.qty += Number(c.quantity || c.orderQty || 0);
      acc.baseQty += Number(c.baseQty || 0);
      acc.newQty += Number(c.newQty || 0);
      acc.rentalQty += Number(c.rentalQty || 0);
      acc.liftedGood += Number(c.liftedGood || 0);
      acc.liftedDamage += Number(c.liftedDamage || 0);
      acc.totalLifted += Number(c.totalLifted || 0);
      acc.totalActive += mode === 'pickup' ? getPickupActiveQty(c) : Number(c.totalActive || 0);
      acc.finalMissing += Number(c.finalMissing || 0);
      
      const weight = Number(c.weight || 0);
      const rowQty = mode === 'moc' ? (Number(c.newQty || 0) + Number(c.rentalQty || 0)) : Number(c.quantity || c.orderQty || (c.baseQty * multiplier) || 0);
      acc.totalWeight += rowQty * weight;
      
      return acc;
    }, { qty: 0, baseQty: 0, newQty: 0, rentalQty: 0, liftedGood: 0, liftedDamage: 0, totalLifted: 0, totalActive: 0, finalMissing: 0, totalWeight: 0 });
  }, [components, multiplier, mode]);

  if (!components || components.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
        <FileText size={40} style={{ marginBottom: '10px', opacity: 0.5 }} />
        <p>No components added yet.</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '50px' }}>Sr.</th>
            <th style={{ ...thStyle, textAlign: 'left', width: '120px' }}>SKU</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Component Name</th>
            
            {/* Mode-Specific Headers */}
            {mode === 'sales' && (
              <>
                <th style={thStyle}>Base Qty</th>
                <th style={thStyle}>Total Qty</th>
              </>
            )}

            {mode === 'moc' && (
              <>
                <th style={thStyle}>Order Qty</th>
                <th style={thStyle}>New</th>
                <th style={thStyle}>Rental</th>
                <th style={thStyle}>Tick</th>
                <th style={thStyle}>Weight (kg)</th>
                <th style={thStyle}>Total (kg)</th>
              </>
            )}

            {mode === 'pickup' && (
              <>
                <th style={thStyle}>Material Qty</th>
                <th style={{ ...thStyle, color: '#16a34a' }}>Good</th>
                <th style={{ ...thStyle, color: '#dc2626' }}>Damage</th>
                <th style={{ ...thStyle, color: '#f59e0b' }}>Missing</th>
                <th style={{ ...thStyle, fontWeight: 'bold' }}>Total Lifted</th>
                <th style={thStyle}>Active</th>
              </>
            )}

            {mode === 'transfer' && (
              <>
                <th style={thStyle}>Base Qty</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Remarks</th>
              </>
            )}

            {mode === 'preview' && (
              <>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Weight</th>
              </>
            )}

            {!hideActions && onDelete && <th style={{ ...thStyle, width: '60px' }}></th>}
          </tr>
        </thead>
        <tbody>
          {components.map((c, idx) => {
            const rowTotalWeight = ((Number(c.newQty || 0) + Number(c.rentalQty || 0)) * Number(c.weight || 0)).toFixed(2);
            
            return (
              <tr key={c.id || idx}>
                <td style={tdStyle}>{idx + 1}</td>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600' }}>{c.sku}</td>
                <td style={{ ...tdStyle, textAlign: 'left' }}>
                  {mode === 'transfer' ? (c.description || c.name || c.sku) : (c.name || c.description)}
                </td>

                {/* Mode-Specific Row Data */}
                {mode === 'sales' && (
                  <>
                    <td style={tdStyle}>
                      <input 
                        type="number" 
                        value={c.baseQty} 
                        onChange={(e) => onChange(idx, 'baseQty', e.target.value)} 
                        style={inputStyle} 
                      />
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{Math.round(c.baseQty * multiplier)}</td>
                  </>
                )}

                {mode === 'moc' && (
                  <>
                    <td style={{ ...tdStyle, color: '#64748b', fontWeight: 'bold' }}>{c.orderQty || 0}</td>
                    <td style={tdStyle}>
                      <input type="number" value={c.newQty} onChange={(e) => onChange(idx, 'newQty', e.target.value)} style={inputStyle} />
                    </td>
                    <td style={tdStyle}>
                      <input type="number" value={c.rentalQty} onChange={(e) => onChange(idx, 'rentalQty', e.target.value)} style={inputStyle} />
                    </td>
                    <td style={tdStyle}>
                      <input type="checkbox" checked={c.ticked} onChange={(e) => onChange(idx, 'ticked', e.target.checked)} />
                    </td>
                    <td style={tdStyle}>{Number(c.weight || 0).toFixed(2)}</td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{rowTotalWeight}</td>
                  </>
                )}

                {mode === 'pickup' && (
                  <>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        value={c.quantity}
                        onChange={(e) => onChange(idx, 'quantity', e.target.value)}
                        style={{ ...inputStyle, backgroundColor: pickupMaterialQtyReadOnly ? '#f8fafc' : inputStyle.backgroundColor }}
                        readOnly={pickupMaterialQtyReadOnly}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input type="number" value={c.liftedGood} onChange={(e) => onChange(idx, 'liftedGood', e.target.value)} style={{ ...inputStyle, backgroundColor: pickupOutcomeReadOnly ? '#f8fafc' : inputStyle.backgroundColor }} readOnly={pickupOutcomeReadOnly} />
                    </td>
                    <td style={tdStyle}>
                      <input type="number" value={c.liftedDamage} onChange={(e) => onChange(idx, 'liftedDamage', e.target.value)} style={{ ...inputStyle, backgroundColor: pickupOutcomeReadOnly ? '#f8fafc' : inputStyle.backgroundColor }} readOnly={pickupOutcomeReadOnly} />
                    </td>
                    <td style={tdStyle}>
                      <input type="number" value={c.finalMissing} onChange={(e) => onChange(idx, 'finalMissing', e.target.value)} style={{ ...inputStyle, backgroundColor: pickupOutcomeReadOnly ? '#f8fafc' : inputStyle.backgroundColor }} readOnly={pickupOutcomeReadOnly} />
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{c.totalLifted}</td>
                    <td style={tdStyle}>{getPickupActiveQty(c)}</td>
                  </>
                )}

                {mode === 'transfer' && (
                  <>
                    <td style={tdStyle}>
                      <input 
                        type="number" 
                        value={c.baseQty} 
                        onChange={(e) => onChange(idx, 'baseQty', e.target.value)} 
                        style={inputStyle} 
                      />
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{Math.round(c.baseQty * multiplier)}</td>
                    <td style={tdStyle}>
                      <input 
                        type="text" 
                        value={c.remarks || ''} 
                        onChange={(e) => onChange(idx, 'remarks', e.target.value)} 
                        style={{ ...inputStyle, width: '120px', textAlign: 'left' }} 
                        placeholder="Notes..."
                      />
                    </td>
                  </>
                )}

                {mode === 'preview' && (
                  <>
                    <td style={tdStyle}>{c.quantity || c.orderQty || 0}</td>
                    <td style={tdStyle}>{(Number(c.quantity || c.orderQty || 0) * Number(c.weight || 0)).toFixed(2)}</td>
                  </>
                )}

                {!hideActions && onDelete && (
                  <td style={tdStyle}>
                    <button 
                      onClick={() => onDelete(idx)} 
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '5px' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}

          {/* Totals Footer */}
          {showTotals && (
            <tr style={{ backgroundColor: '#f8fafc', fontWeight: '700' }}>
              <td colSpan="3" style={{ ...tdStyle, textAlign: 'right' }}>Total Summary</td>
              
              {mode === 'sales' && (
                <>
                  <td style={tdStyle}>{totals.baseQty}</td>
                  <td style={tdStyle}>{Math.round(totals.baseQty * multiplier)}</td>
                </>
              )}

              {mode === 'moc' && (
                <>
                  <td style={tdStyle}>-</td>
                  <td style={tdStyle}>{totals.newQty}</td>
                  <td style={tdStyle}>{totals.rentalQty}</td>
                  <td style={tdStyle}>-</td>
                  <td style={tdStyle}>-</td>
                  <td style={tdStyle}>{totals.totalWeight.toFixed(2)} kg</td>
                </>
              )}

              {mode === 'pickup' && (
                <>
                  <td style={tdStyle}>{totals.qty}</td>
                  <td style={tdStyle}>{totals.liftedGood}</td>
                  <td style={tdStyle}>{totals.liftedDamage}</td>
                  <td style={tdStyle}>{totals.finalMissing}</td>
                  <td style={tdStyle}>{totals.totalLifted}</td>
                  <td style={tdStyle}>{totals.totalActive}</td>
                </>
              )}

              {mode === 'preview' && (
                <>
                  <td style={tdStyle}>{totals.qty}</td>
                  <td style={tdStyle}>{totals.totalWeight.toFixed(2)} kg</td>
                </>
              )}

              {mode === 'transfer' && (
                <>
                  <td style={tdStyle}>{totals.baseQty}</td>
                  <td style={tdStyle}>{Math.round(totals.baseQty * multiplier)}</td>
                  <td style={tdStyle}></td>
                </>
              )}

              {!hideActions && onDelete && <td style={tdStyle}></td>}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PartsConfigTable;
